import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { LogoFull } from '@/components/LogoFull';
import { Button } from '@/components/ui/Button';
import { trackAnonymous } from '@/components/PostHogTracker';

export function VerifyEmailPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setError('Missing verification token.');
            return;
        }

        const key = `verify_${token.slice(0, 16)}`;
        if (sessionStorage.getItem(key)) {
            setStatus('success');
            return;
        }

        api.get(`/api/verify-email?token=${encodeURIComponent(token)}`)
            .then(() => {
                sessionStorage.setItem(key, '1');
                trackAnonymous('email_verified');
                setStatus('success');
            })
            .catch(() => {
                if (sessionStorage.getItem(key)) {
                    setStatus('success');
                } else {
                    setStatus('error');
                    setError('Invalid or expired verification link.');
                    trackAnonymous('email_verification_failed');
                }
            });
    }, [token]);

    if (status === 'pending') {
        return null;
    }

    if (status === 'success') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Email verified
                        </h1>

                        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            Your email has been verified successfully. You can now log in to your account.
                        </div>

                        <Link to="/login" className="block">
                            <Button type="button" className="w-full" size="lg">
                                Go to login
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <LogoFull className="mx-auto h-10 w-auto" />
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Verification failed
                    </h1>

                    <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {error}
                    </div>

                    <Link to="/login" className="block">
                        <Button type="button" variant="secondary" className="w-full" size="lg">
                            Back to login
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
